import { createContext, useState, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';

import { authenticate } from './authenticate';
import { index } from './base-rest';
import { getProfileMeFull, getThumbnailSW3Url } from './user.service';

export const AuthContext = createContext({});

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState();
  const [thumbnailProfile, setThumbnailProfile] = useState(null);
  const isAuthenticated = !!user;
  const [changingPassword, setChangingPassword] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const changeThumbnailProfile = useCallback(async () => {
    const response_thumbnail = await getThumbnailSW3Url();

    if (!response_thumbnail?.error) setThumbnailProfile(response_thumbnail);

    setLoading(false);
  }, []);

  const prepareCityProfile = useCallback(async (profile) => {
    const response_units = await index('units', { per_page: 500 });

    // Itera apenas unidades ATIVAS e para no primeiro match — evita que
    // vinculos legados (vendedor transferido sem desvincular da unidade
    // antiga) sobrescrevam profile.city de forma nao-deterministica.
    // Incidente: Cristiane Warmling (2026-06-19) — vinculos simultaneos
    // em Videira (legado) e Chapeco, fazendo orders novos serem criados
    // ora em uma cidade, ora em outra dependendo da ordem do GET /units.
    const units = (response_units?.rows ?? []).filter((u) => u.is_active);

    for (let i = 0; i < units.length; i++) {
      const unit = units[i];

      // eslint-disable-next-line no-await-in-loop
      const sellers = await index(`units/${unit.id}/sellers`);

      const matched = sellers?.some((s) => s.username === profile.username);
      if (matched) {
        profile.city = unit.city;
        break;
      }
    }

    return profile;
  }, []);

  const signIn = async ({ username, password }) => {
    const response = await authenticate(username, password);

    if (!response.error) {
      localStorage.setItem(
        'vo_access_token',
        JSON.stringify(response.access_token)
      );

      // Refresh token introduzido em 2026-05-26 (FEAT-jwt-access-refresh).
      // Login antigo nao retornava refresh_token; guard para nao quebrar
      // se o backend ainda nao foi deployado.
      if (response.refresh_token) {
        localStorage.setItem(
          'vo_refresh_token',
          JSON.stringify(response.refresh_token)
        );
      }

      changeThumbnailProfile();

      let profile = await getProfileMeFull();

      if (profile.roles.findIndex((item) => item.slug === 'seller') > -1) {
        profile = await prepareCityProfile(profile);
      }

      setUser(profile);
    }

    return response;
  };

  const signOut = useCallback(() => {
    // Libera o blob URL da thumbnail antes de limpar.
    if (thumbnailProfile) URL.revokeObjectURL(thumbnailProfile);

    // Limpa storages inteiros — defensivo contra qualquer item futuro
    // que venha a ser persistido alem do vo_access_token.
    localStorage.clear();
    sessionStorage.clear();

    // Forca refresh completo da pagina:
    // - Descarta qualquer estado React, memos, closures e contextos.
    // - Faz o navegador pedir um index.html novo (que aponta para o JS
    //   atualizado caso haja deploy mais recente), evitando que o
    //   usuario fique preso em uma versao antiga apos relogar.
    window.location.href = '/';
  }, [thumbnailProfile]);

  const reloadUser = useCallback(async () => {
    setLoading(true);

    let profile = await getProfileMeFull();

    if (!profile.error) {
      if (profile.roles.findIndex((item) => item.slug === 'seller') > -1) {
        profile = await prepareCityProfile(profile);
      }

      setUser(profile);

      changeThumbnailProfile();
    }

    setLoading(false);
  }, []);

  const handleChangingPassword = useCallback((value) => {
    setChangingPassword(value);
  }, []);

  const hasRole = useCallback(
    (value) => user.roles.findIndex((item) => item.slug === value) > -1,
    [user]
  );

  const hasRoles = useCallback(
    (values) => {
      const have_roles = user.roles.filter((item) =>
        values.includes(item.slug)
      );

      return have_roles.length >= 1;
    },
    [user]
  );

  const hasPermissions = useCallback(
    (values) => {
      const all_permissions = user.permissions.concat(user.extra_permissions);

      const have_permissions = all_permissions.filter((item) =>
        values.includes(item.slug)
      );

      return have_permissions.length >= 1;
    },
    [user]
  );

  useEffect(() => {
    const access_token = JSON.parse(localStorage.getItem('vo_access_token'));
    if (access_token) reloadUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        isLoading,
        changingPassword,
        thumbnailProfile,
        signIn,
        signOut,
        handleChangingPassword,
        hasRole,
        setLoading,
        changeThumbnailProfile,
        hasPermissions,
        hasRoles,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthProvider;
